/* The base style, and the CHS ENC chart source.
 *
 * MAPLIBRE IS PINNED TO 5.21.1 — do not bump it without checking depth shading.
 *
 * `color-relief` renders nothing on 6.1.0. Verified by elimination: identical
 * source spec, identical layer spec, identical colour ramp taken verbatim from
 * Open Waters' own published style, layer present in the stack in the right
 * band, source reporting loaded, DEM tiles fetching, zero console errors or
 * warnings — and no pixels. The same code on 5.21.1 renders correctly, which is
 * also the version their working demo ships.
 *
 * 6.0.0 added "validate raster-dem sources passed to map.addSource, which were
 * previously skipped", so that is the likely neighbourhood, but nothing is
 * logged either way. Re-test on a later 6.x before upgrading; 5.21 has
 * everything needed (color-relief since 5.6, resampling since 5.20).
 *
 * MapLibre has no WMS helper, so the chart is a raster source with an XYZ
 * template carrying `{bbox-epsg-3857}` — MapLibre substitutes the tile's bbox
 * per request. That is the documented way to consume WMS here, and it happens
 * to suit us: per-tile GetMap requests land on the same fixed grid the pi's
 * chart cache already stores, whereas a single-image ImageWMS would miss it
 * entirely.
 */

import type { StyleSpecification } from 'maplibre-gl';

import { sources } from '../sources.ts';

/** The DFO/CHS ENC Maritime Chart Service, asked to render as a chart. */
export const WMS_BASE =
  'https://egisp.dfo-mpo.gc.ca/arcgis/rest/services/chs/ENC_MaritimeChartService/MapServer/exts/MaritimeChartService/WMSServer';

export const CHS_LAYERS = '0,1,2,3,4,5,6,7,10,11';

export function wmsTemplate(base: string, layers: string): string {
  return (
    `${base}?service=WMS&version=1.3.0&request=GetMap&layers=${layers}&styles=` +
    '&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256' +
    '&format=image/png&transparent=true'
  );
}

/** Through the tile cache when there is one, direct to CHS when there is not.
 *
 *  `useCache` is the caller's decision (the chart layer stands the cache down
 *  when it starts answering 404s); `sources().has` is whether a cache exists to
 *  decide about at all. A hosted chart with no proxy takes the second branch
 *  and works — which is why that branch was already here. */
export function chartTiles(useCache: boolean, generation = 0, chart = 'chs-enc'): string {
  const s = sources();
  /* `{chart}/{z}/{x}/{y}` — the proxy takes the chart in the path so ONE
   * deployment can front several, which is what lets the hosted map swap
   * charts at the border without a second origin. The pi's plugin serves only
   * CHS and ignores the extra segment. */
  return useCache && s.has('chart-cache')
    ? `${s.base('chart-cache')}/${chart}/{z}/{x}/{y}?g=${generation}`
    : wmsTemplate(WMS_BASE, CHS_LAYERS);
}

/* An empty base. The chart is a layer like any other, so the style itself
 * carries only the background — swapping charts must not mean rebuilding the
 * style and losing every overlay. */
export function baseStyle(night: boolean, base = '/'): StyleSpecification {
  return {
    version: 8,
    /* Glyphs are SELF-HOSTED. Soundings and contour labels are symbol layers
     * and cannot render text without them, and Open Waters' own style points at
     * https://demotiles.maplibre.org — a demo server with no SLA, reachable
     * only with internet. On a boat that means the depth numbers vanish exactly
     * when you are somewhere unfamiliar with no signal.
     *
     * One 77 kB file covers Latin 0-255, which is every character a sounding
     * ("12.4") or a contour label ("20m") can contain. */
    glyphs: `${base}glyphs/{fontstack}/{range}.pbf`,
    sources: {},
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: { 'background-color': night ? '#060a11' : '#edefe8' }
      }
    ]
  };
}
